/* PDP clone kit – core behaviours loaded on every page (kept tiny).
   Section-specific code lives in pdp-main.js, pdp-steps.js and pdp-reviews.js and is only loaded by those sections.
   Rules: no scroll listeners for visibility, no idle animation loops, passive listeners only. */
(function () {
  'use strict';

  window.PDP = window.PDP || {};
  PDP.define = function (name, ctor) {
    if (!window.customElements || customElements.get(name)) return;
    customElements.define(name, ctor);
  };
  PDP.reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  /* Animated <details> used by accordions, FAQ, stats and week-by-week (delegated: one listener for the page). */
  function animateDetails(details, open) {
    if (details._pdpAnim) details._pdpAnim.cancel();
    var summary = details.querySelector(':scope > summary');
    if (!summary || PDP.reduceMotion || !details.animate) {
      details.open = open;
      return;
    }
    var start = details.offsetHeight;
    var end;
    details.style.overflow = 'hidden';
    if (open) {
      details.open = true;
      end = details.offsetHeight;
    } else {
      end = summary.offsetHeight + (details.offsetHeight - details.clientHeight);
    }
    var anim = details.animate({ height: [start + 'px', end + 'px'] }, { duration: 320, easing: 'cubic-bezier(.4,0,.2,1)' });
    details._pdpAnim = anim;
    anim.onfinish = function () {
      details._pdpAnim = null;
      if (!open) details.open = false;
      details.style.overflow = '';
    };
    anim.oncancel = function () { details._pdpAnim = null; details.style.overflow = ''; };
  }

  document.addEventListener('click', function (e) {
    var summary = e.target.closest && e.target.closest('details.pdp-anim > summary');
    if (!summary) return;
    var details = summary.parentElement;
    e.preventDefault();
    var willOpen = details._pdpAnim ? !details._pdpTarget : !details.open;
    details._pdpTarget = willOpen;
    var group = details.closest('[data-exclusive]');
    if (willOpen && group) {
      group.querySelectorAll('details.pdp-anim[open]').forEach(function (d) {
        if (d !== details && d.closest('[data-exclusive]') === group) {
          d._pdpTarget = false;
          animateDetails(d, false);
        }
      });
    }
    animateDetails(details, willOpen);
  });

  /* Header: mobile menu drawer */
  PDP.define('pdp-header-drawer', class extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.panel = this.querySelector('[data-drawer]');
      this.querySelectorAll('[data-drawer-open]').forEach(function (b) { b.addEventListener('click', function () { self.toggle(true); }); });
      this.querySelectorAll('[data-drawer-close]').forEach(function (b) { b.addEventListener('click', function () { self.toggle(false); }); });
      // Esc: the <dialog> asks to close; slide out instead of vanishing
      if (this.panel) this.panel.addEventListener('cancel', function (e) { e.preventDefault(); self.toggle(false); });
    }
    toggle(open) {
      var d = this.panel;
      if (!d) return;
      clearTimeout(this._t);
      if (open) {
        if (!d.open) d.showModal();
        requestAnimationFrame(function () { d.classList.add('is-open'); });
      } else {
        d.classList.remove('is-open');
        this._t = setTimeout(function () { if (d.open) d.close(); }, 350);
      }
      document.documentElement.classList.toggle('pdp-lock', open);
      var opener = this.querySelector('[data-drawer-open]');
      if (opener) opener.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
  });

  /* Carousel: a native scroll-snap track ([data-track] > [data-slide]) with optional arrows ([data-prev]/[data-next]),
     dots ([data-dots], one per slide) and a progress line ([data-progress] > span). Arrows scroll by one slide and wrap
     around at both ends. The
     active slide comes from an IntersectionObserver on the track; the progress line is updated in a scroll listener that
     only runs while the track scrolls (passive, one rAF per frame). */
  PDP.define('pdp-carousel', class extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.track = this.querySelector('[data-track]');
      if (!this.track || this._ready) return;
      this._ready = true;
      this.slides = Array.prototype.slice.call(this.track.querySelectorAll('[data-slide]'));
      this.index = 0;
      var prev = this.querySelector('[data-prev]');
      var next = this.querySelector('[data-next]');
      if (prev) prev.addEventListener('click', function () { self.step(-1); });
      if (next) next.addEventListener('click', function () { self.step(1); });
      this.dotsWrap = this.querySelector('[data-dots]');
      if (this.dotsWrap && this.slides.length > 1) {
        this.dots = this.slides.map(function (s, i) {
          var b = document.createElement('button');
          b.type = 'button';
          b.setAttribute('aria-label', 'Go to slide ' + (i + 1));
          b.addEventListener('click', function () { self.goTo(i); });
          self.dotsWrap.appendChild(b);
          return b;
        });
      }
      this.bar = this.querySelector('[data-progress] > span');
      if (this.bar) {
        var raf = 0;
        this.track.addEventListener('scroll', function () {
          if (raf) return;
          raf = requestAnimationFrame(function () { raf = 0; self.paintBar(); });
        }, { passive: true });
        this.paintBar();
      }
      if ('IntersectionObserver' in window && this.slides.length > 1) {
        this._io = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            if (en.isIntersecting && en.intersectionRatio >= 0.6) self.setActive(self.slides.indexOf(en.target));
          });
        }, { root: this.track, threshold: [0.6] });
        this.slides.forEach(function (s) { self._io.observe(s); });
      }
      this.setActive(0);
    }
    disconnectedCallback() { if (this._io) this._io.disconnect(); }
    setActive(i) {
      if (i < 0) return;
      this.index = i;
      if (this.dots) this.dots.forEach(function (d, k) { d.classList.toggle('is-active', k === i); });
    }
    goTo(i) {
      var s = this.slides[i];
      if (!s) return;
      this.track.scrollTo({ left: s.offsetLeft - this.slides[0].offsetLeft, behavior: PDP.reduceMotion ? 'auto' : 'smooth' });
      this.setActive(i);
    }
    step(d) {
      var n = this.slides.length;
      if (!n) return;
      // at an end the arrows wrap around (data-wrap: "both" by default, "desktop" = from 750 px up, "none")
      var wrap = this.dataset.wrap || 'both';
      var canWrap = wrap === 'both' || (wrap === 'desktop' && !!window.matchMedia && window.matchMedia('(min-width: 750px)').matches);
      var t = this.track, max = t.scrollWidth - t.clientWidth;
      // a snap point can sit a few pixels in from either end (track padding)
      var atStart = t.scrollLeft <= 16, atEnd = t.scrollLeft >= max - 16;
      if ((d < 0 && atStart) || (d > 0 && atEnd)) {
        if (!canWrap) return;
        if (this.dataset.step === 'page') { t.scrollTo({ left: d < 0 ? max : 0, behavior: PDP.reduceMotion ? 'auto' : 'smooth' }); return; }
        this.goTo(d < 0 ? n - 1 : 0);
        return;
      }
      if (this.dataset.step === 'page') {
        // one page (the visible width) per click
        t.scrollTo({ left: t.scrollLeft + d * t.clientWidth, behavior: PDP.reduceMotion ? 'auto' : 'smooth' });
        return;
      }
      this.goTo(Math.max(0, Math.min(n - 1, this.index + d)));
    }
    paintBar() {
      var t = this.track;
      var max = t.scrollWidth - t.clientWidth;
      var vis = t.scrollWidth ? t.clientWidth / t.scrollWidth : 1;
      var pos = max > 0 ? t.scrollLeft / max : 0;
      this.bar.style.width = (vis * 100) + '%';
      this.bar.style.transform = 'translateX(' + (pos * (1 / vis - 1) * 100) + '%)';
    }
  });

  /* Toggle buttons ([data-toggle] with aria-controls): show/hide the panel, flip aria-expanded and put is-open on the
     closest [data-toggle-scope]. One delegated listener for the page. */
  document.addEventListener('click', function (e) {
    var b = e.target.closest && e.target.closest('[data-toggle]');
    if (!b) return;
    var panel = document.getElementById(b.getAttribute('aria-controls'));
    var open = b.getAttribute('aria-expanded') !== 'true';
    b.setAttribute('aria-expanded', open ? 'true' : 'false');
    var scope = b.closest('[data-toggle-scope]');
    if (scope) scope.classList.toggle('is-open', open);
    if (panel) panel.hidden = !open;
  });

  /* Marquees and other endless CSS animations ([data-pdp-pause], e.g. a wiggling button): paused while off-screen
     (.is-paused), so they cost nothing there. A marquee with data-speed / data-speed-mobile (pixels per second) gets its
     loop time from its track: half the track is one loop, so the row moves at that speed whatever its items are. The
     time is measured again when the track changes size (fonts, images, editor changes) or the width class changes. */
  var wide = window.matchMedia ? window.matchMedia('(min-width: 750px)') : null;
  function marqueeSpeed(m) {
    var track = m.firstElementChild;
    var v = parseFloat(wide && !wide.matches ? (m.dataset.speedMobile || m.dataset.speed) : m.dataset.speed);
    if (!track || !v) return;
    var half = track.offsetWidth / 2;
    if (half > 0) track.style.setProperty('--mq-dur', (half / v).toFixed(2) + 's');
  }
  function initMarquees(root) {
    (root || document).querySelectorAll('[data-pdp-marquee][data-speed]').forEach(function (m) {
      if (m._pdpSpeed) return;
      m._pdpSpeed = true;
      marqueeSpeed(m);
      if (window.ResizeObserver && m.firstElementChild) new ResizeObserver(function () { marqueeSpeed(m); }).observe(m.firstElementChild);
      if (wide && wide.addEventListener) wide.addEventListener('change', function () { marqueeSpeed(m); });
    });
    if (!('IntersectionObserver' in window)) return;
    var holders = Array.prototype.slice.call((root || document).querySelectorAll('[data-pdp-marquee], [data-pdp-pause], .pdp-h2'));
    holders.filter(function (m) { return !m.classList.contains('pdp-h2') || m.querySelector('i, em'); }).forEach(function (m) {
      if (m._pdpObs) return;
      m._pdpObs = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { m.classList.toggle('is-paused', !en.isIntersecting); });
      });
      m._pdpObs.observe(m);
    });
  }

  /* Sticky announcement bar height -> --pdp-ann-h (the sticky header sits right below it). */
  function initAnnouncement(root) {
    var el = (root || document).querySelector('[data-pdp-ann][data-ann-sticky="true"]');
    if (!el || el._pdpRO || !window.ResizeObserver) return;
    el._pdpRO = new ResizeObserver(function () {
      document.documentElement.style.setProperty('--pdp-ann-h', el.offsetHeight + 'px');
    });
    el._pdpRO.observe(el);
  }

  /* In-view: [data-inview] gets .is-in once 35% of it is visible (charts that draw themselves, bars that grow). Inside it,
     [data-count] numbers count up from 0 to their own value (decimals and separator kept); text that isn't a number
     (e.g. a placeholder like XX.X) is left as it is. Animation frames run only during the count. */
  function countUp(el) {
    var raw = el.textContent.trim();
    var m = raw.match(/^(\d+)([.,](\d+))?$/);
    if (!m || PDP.reduceMotion) return;
    var target = parseFloat(m[1] + (m[3] ? '.' + m[3] : ''));
    var dec = m[3] ? m[3].length : 0;
    var sep = m[2] ? m[2].charAt(0) : '.';
    var t0 = null;
    function frame(t) {
      if (!t0) t0 = t;
      var k = Math.min(1, (t - t0) / 1400);
      var v = target * (1 - Math.pow(1 - k, 3));
      el.textContent = v.toFixed(dec).replace('.', sep);
      if (k < 1) requestAnimationFrame(frame);
      else el.textContent = raw;
    }
    requestAnimationFrame(frame);
  }
  function initInView(root) {
    var els = (root || document).querySelectorAll('[data-inview]:not(.is-in)');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) { els.forEach(function (el) { el.classList.add('is-in'); }); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        en.target.classList.add('is-in');
        en.target.querySelectorAll('[data-count]').forEach(countUp);
        io.unobserve(en.target);
      });
    }, { threshold: 0.35 });
    els.forEach(function (el) { io.observe(el); });
  }

  function init(root) { initMarquees(root); initAnnouncement(root); initInView(root); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); });
  else init(document);
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
